<template>

  <div>
    <b-row v-if="$permissions().hasPermission('create special-commodities')">
      <b-col class="text-left">
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="flat-primary"
          class="my-1"
          @click="showFilters = !showFilters"
        >
          <FeatherIcon icon="FilterIcon" />
          Filters
        </b-button>
      </b-col>
      <b-col class="text-right">
        <b-button
          variant="primary"
          class="my-1"
          @click="$router.push('/special-commodities/add')"
        >
          Add commodity
        </b-button>
      </b-col>
    </b-row>
    <b-card
      v-if="showFilters"
      id="filters-card"
      title="Filters"
    >
      <b-form @keyup.enter="fetchList">
        <b-row>
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Country"
              rules="min:2|max:2"
            >
              <b-form-group
                label="Country"
                label-for="country"
              >
                <b-form-input
                  id="country"
                  v-model="filters.country"
                  name="country"
                  autocomplete="off"
                  :state="errors.length > 0 ? false:null"
                />
              </b-form-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-col>
          <b-col>
            <b-form-group
              label="Type"
              label-for="type"
            >
              <v-select
                id="type"
                v-model="filters.type"
                :reduce="option => option.value"
                label="text"
                multiple
                :options="typeList"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="HS code"
              label-for="hs-code"
            >
              <b-form-input
                id="hs-code"
                v-model="filters.hsCode"
                name="HS code"
                autocomplete="off"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="text-right">
            <b-button
              type="button"
              variant="primary"
              @click="fetchList()"
            >
              Filter
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="overflow-x-scroll">
        <b-table
          ref="refUserListTable"
          class="position-relative"
          :items="commodityList"
          :fields="tableColumns"
          primary-key="id"
          :busy="loading"
          show-empty
          empty-text="No matching records found"
        >

          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-1" />
              <strong>Loading...</strong>
            </div>
          </template>

          <!-- Column: Created at -->
          <template #cell(created_at)="data">
            {{ data.item.created_at | formatDate }}
          </template>

          <template #cell(type)="data">
            <b-badge
              v-if="data.item.type === 'reduced_vat'"
              pill
              variant="light-primary"
            >
              Reduced VAT Tax
            </b-badge>
            <b-badge
              v-if="data.item.type === 'prohibited'"
              pill
              variant="light-danger"
            >
              Prohibited
            </b-badge>
            <b-badge
              v-if="data.item.type === 'replace_hs_code'"
              pill
              variant="light-primary"
            >
              Replace HS code
            </b-badge>
            <b-badge
              v-if="data.item.type === 'replace_description'"
              pill
              variant="light-primary"
            >
              Replace Description
            </b-badge>
          </template>

          <template #cell(additional_data)="data">
            <b-badge
              v-if="data.item.additional_data && data.item.additional_data.reduced_vat"
              pill
              variant="light-primary"
            >
              ReducedVat code: {{ data.item.additional_data.reduced_vat }}
            </b-badge>
            <b-badge
              v-if="data.item.additional_data && data.item.additional_data.origin_country"
              pill
              variant="light-danger"
            >
              Origin country: {{ data.item.additional_data.origin_country }}
            </b-badge>
            <b-badge
              v-if="data.item.additional_data && data.item.additional_data.replace_hs_code_to"
              pill
              variant="light-primary"
            >
              Replace HS code to: {{ data.item.additional_data.replace_hs_code_to }}
            </b-badge>
            <b-badge
              v-if="data.item.additional_data && data.item.additional_data.replace_description_to"
              pill
              variant="light-primary"
            >
              Replace Description to: {{ data.item.additional_data.replace_description_to }}
            </b-badge>
          </template>

          <template #cell(logo)="data">
            <CompanyThumb
              :logo="data.item.company.logo"
              :name="data.item.company.name"
            />
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-col>
              <b-button-group>
                <b-button
                  v-if="$permissions().hasPermission('edit special-commodities')"
                  variant="primary"
                  size="sm"
                  @click="edit(data.item.id)"
                >
                  <feather-icon
                    icon="EditIcon"
                    class="d-inline"
                  />
                </b-button>
                <b-button
                  v-if="$permissions().hasPermission('delete special-commodities')"
                  v-b-tooltip.hover.top="'Delete special good'"
                  variant="outline-danger"
                  size="sm"
                  @click="deleteGoods(data.item.id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="d-inline"
                  />
                </b-button>
              </b-button-group>
            </b-col>
          </template>

        </b-table>
        <pagination />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, VBTooltip, BBadge, BButtonGroup, BForm, BFormGroup, BFormInput, BSpinner,
} from 'bootstrap-vue'
import handleError from '@/views/components/errorHandler'
import CompanyThumb from '@/views/components/CompanyThumb.vue'
import vSelect from 'vue-select'
import { parseFilters } from '@core/utils/filter'
import { ValidationProvider } from 'vee-validate/dist/vee-validate.full'
import Ripple from 'vue-ripple-directive'
import { EventBus } from '@core/utils/utils'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'

export default {
  components: {
    Pagination,
    BSpinner,
    ValidationProvider,
    BFormInput,
    vSelect,
    BFormGroup,
    BForm,
    CompanyThumb,
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BButtonGroup,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    const tableColumns = [
      { key: 'logo', label: 'Company', sortable: false, class: 'image-column' },
      { key: 'country_code', label: 'Country', sortable: true },
      { key: 'hs_code', label: 'HS Code', sortable: true },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'description', label: 'Description', sortable: true },
      { key: 'additional_data', label: 'Additional', sortable: true },
      { key: 'created_at', sortable: true },
    ]
    if (
      this.$permissions().hasPermission('edit special-commodities')
      || this.$permissions().hasPermission('delete special-commodities')
    ) {
      tableColumns.push({ key: 'actions' })
    }
    return {
      loading: false,
      showFilters: true,
      companyList: [],
      typeList: this.$classifiers().getOptions('special_commodities.types', false),
      specialCommodities: [],
      tableColumns: tableColumns,
      filters: {
        country: null,
        type: null,
        hsCode: null,
        page: 1,
      },
    }
  },
  computed: {
    commodityList() {
      return this.specialCommodities
    },
  },
  async created() {
    EventBus.$on('companyListUpdated', this.fetchList)
    EventBus.$on('pageChange', this.handlePageChange)
    this.fetchList()
  },
  beforeDestroy() {
    EventBus.$off('companyListUpdated', this.fetchList)
    EventBus.$off('pageChange', this.handlePageChange)
  },
  methods: {
    handlePageChange(page) {
      this.filters.page = page
      this.fetchList(false)
    },
    edit(id) {
      this.$router.push({ name: 'edit-special-commodities', query: { specialCommoditiesId: id } })
    },
    deleteGoods(id) {
      this.$http.delete(`/v1/special-commodities/${id}`)
        .then(() => {
          this.$router.push({ name: 'special-commodities' })
          this.fetchList()
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    fetchList(resetPaginator = true) {
      if (resetPaginator) {
        this.filters.page = 1
      }
      this.loading = true
      this.$http.get('/v1/special-commodities', {
        params: parseFilters(this.filters),
      })
        .then(response => {
          this.$data.specialCommodities = response.data.data
          EventBus.$emit('updateMeta', response.data.meta)
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
